import classNames from 'classnames';
import React, { FC } from 'react';
import { TypeBottleProductCardFields } from '@/core/generated-types';
import {
  ChevronIcon,
  HeaderText,
  InternalLink,
  LuvImage,
  RichText,
} from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { formatImageObject } from '@/utils/helpers';
import { Document } from '@contentful/rich-text-types';

export const badgePositionClasses = {
  left: `top-[-140px] left-[-28px] md:left-[-45px] rotate-[-13deg]`,
  right: `top-[-180px] left-[215px] md:left-[215px] rotate-[16deg]`,
};

const BottleProductCard: FC<TypeBottleProductCardFields> = ({
  image,
  badgePosition,
  badgeText,
  badgeColor,
  entryName,
  title,
  valueProps,
  link,
}) => {
  const cardImage = formatImageObject(
    image?.fields?.Image?.fields?.title,
    image?.fields?.Image?.fields?.file?.url,
  );

  return (
    <InternalLink classes="h-full pt-36" link={link} hideArrow={true}>
      <div
        data-testid={`bottle-product-card-${entryName}`}
        className="flex h-full w-[328px] flex-col rounded bg-white px-4 pb-6 md:w-[360px]"
      >
        <div className="mt-[-136px] h-[272px] w-[272px] self-center">
          <LuvImage
            imageData={cardImage}
            className="h-[272px] w-[272px] rounded-full"
          />
          {badgePosition && badgeText && (
            <div
              className={classNames(
                badgePositionClasses[badgePosition],
                `relative grid h-[100px] w-[100px] items-center justify-center rounded-full p-2 text-center shadow-[0px_1px_2px_rgb(0,0,0,0.25)]`,
                `bg-${badgeColor || 'purple'}`,
              )}
            >
              <span className="self-center text-sm text-white">
                {badgeText}
              </span>
            </div>
          )}
        </div>
        <div className="pt-4 text-center">
          <HeaderText size={HeaderSize.H3} classes="font-bold" text={title} />
        </div>
        <div className="mt-6 grid h-full grid-cols-1 gap-6 text-left">
          {valueProps?.map((item, i) => (
            <div
              data-testid="bottle-product-card-value-prop"
              key={i}
              className={classNames('grid grid-cols-[90px_auto] gap-5')}
            >
              <LuvImage
                width={90}
                height={90}
                className="mr-5  self-center"
                imageData={item?.fields?.image}
              />
              <div className="mr-4">
                <HeaderText
                  size={HeaderSize.H4}
                  classes="font-bold"
                  text={item.fields?.textHeading}
                  hoverEffect
                />
                <span className="text-sm !leading-5">
                  {item.fields?.textBody && (
                    <RichText content={item.fields.textBody as Document} />
                  )}
                </span>
              </div>
            </div>
          ))}
          <span className="ml-[110px] flex flex-row items-center gap-1.5 self-end text-lg font-bold text-indigo hover:opacity-80">
            {link.fields.linkText}
            <ChevronIcon
              color="indigo"
              direction="right"
              height={13}
              width={13}
              strokeWidth={3}
              classes={classNames('relative top-[1px]')}
            />
          </span>
        </div>
      </div>
    </InternalLink>
  );
};

export default React.memo(BottleProductCard);
